/**
 * Grid helpers. Cells are row-major `row * cols + col`, zero-based (CLAUDE.md).
 * Neighbour lists are always returned in ascending cell order.
 */
import type { Cell, Edge } from './types';

export interface GridDims {
  readonly rows: number;
  readonly cols: number;
}

export function cellCount(d: GridDims): number {
  return d.rows * d.cols;
}

export function inBounds(d: GridDims, row: number, col: number): boolean {
  return (
    Number.isInteger(row) && Number.isInteger(col) && row >= 0 && row < d.rows && col >= 0 && col < d.cols
  );
}

export function isCell(d: GridDims, cell: number): boolean {
  return Number.isInteger(cell) && cell >= 0 && cell < d.rows * d.cols;
}

function assertCell(d: GridDims, cell: number): void {
  if (!isCell(d, cell)) throw new RangeError(`cell ${cell} out of range for ${d.rows}x${d.cols}`);
}

export function cellAt(d: GridDims, row: number, col: number): Cell {
  if (!inBounds(d, row, col)) throw new RangeError(`(${row},${col}) out of range for ${d.rows}x${d.cols}`);
  return row * d.cols + col;
}

export function rowOf(d: GridDims, cell: Cell): number {
  assertCell(d, cell);
  return Math.floor(cell / d.cols);
}

export function colOf(d: GridDims, cell: Cell): number {
  assertCell(d, cell);
  return cell % d.cols;
}

export function rowCol(d: GridDims, cell: Cell): [row: number, col: number] {
  return [rowOf(d, cell), colOf(d, cell)];
}

/** Orthogonal neighbours (up, left, right, down), ascending. */
export function orthNeighbors(d: GridDims, cell: Cell): Cell[] {
  const [r, c] = rowCol(d, cell);
  const out: Cell[] = [];
  if (r > 0) out.push(cell - d.cols);
  if (c > 0) out.push(cell - 1);
  if (c < d.cols - 1) out.push(cell + 1);
  if (r < d.rows - 1) out.push(cell + d.cols);
  return out;
}

/** All 8 surrounding cells (king moves), ascending. */
export function neighbors8(d: GridDims, cell: Cell): Cell[] {
  const [r, c] = rowCol(d, cell);
  const out: Cell[] = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue;
      if (inBounds(d, r + dr, c + dc)) out.push((r + dr) * d.cols + (c + dc));
    }
  }
  return out;
}

export function areOrthAdjacent(d: GridDims, a: Cell, b: Cell): boolean {
  if (!isCell(d, a) || !isCell(d, b)) return false;
  const [ra, ca] = rowCol(d, a);
  const [rb, cb] = rowCol(d, b);
  return Math.abs(ra - rb) + Math.abs(ca - cb) === 1;
}

/** Normalized unordered edge: smaller endpoint first. Does not check adjacency. */
export function normalizeEdge(a: Cell, b: Cell): Edge {
  if (a === b) throw new RangeError(`edge endpoints must differ, got ${a}`);
  return a < b ? [a, b] : [b, a];
}

export function isNormalizedEdge(e: readonly [number, number]): boolean {
  return e[0] < e[1];
}

/** Total order on normalized edges: by first endpoint, then second. */
export function compareEdges(x: readonly [number, number], y: readonly [number, number]): number {
  return x[0] - y[0] || x[1] - y[1];
}

/** All orthogonal edges of the grid, normalized and sorted. */
export function allOrthEdges(d: GridDims): Edge[] {
  const out: Edge[] = [];
  for (let cell = 0; cell < d.rows * d.cols; cell++) {
    for (const n of orthNeighbors(d, cell)) if (n > cell) out.push([cell, n]);
  }
  return out.sort(compareEdges);
}
